import { z } from 'zod';

import { captureSentryException } from 'shared/utils';

import { LOGIN_FIELDS, LOGIN_VALIDATION_ERROR_CODES } from '../consts';

const VALIDATABLE_FIELDS = {
  EMAIL: LOGIN_FIELDS.EMAIL,
  PASSWORD: LOGIN_FIELDS.PASSWORD
} as const;

/** A single field-level validation error returned by the login API. */
export interface LoginFieldError {
  /** The form field name that the error is associated with. */
  field: (typeof VALIDATABLE_FIELDS)[keyof typeof VALIDATABLE_FIELDS];
  /** The server validation error code. */
  code: (typeof LOGIN_VALIDATION_ERROR_CODES)[keyof typeof LOGIN_VALIDATION_ERROR_CODES];
}

const LoginServerErrorPayloadSchema = z.object({
  fields: z.record(z.nativeEnum(VALIDATABLE_FIELDS), z.array(z.nativeEnum(LOGIN_VALIDATION_ERROR_CODES)))
});

/** Extracts field errors from the login API 400 payload (`{ code, fields }`) into a flat array. */
export const mapLoginServerErrorsToFieldErrors = (error: unknown): LoginFieldError[] => {
  const result = LoginServerErrorPayloadSchema.safeParse(error);

  if (!result.success) {
    captureSentryException({
      message: 'Unrecognised login validation error',
      feature: 'login',
      error,
      extra: { method: 'mapLoginServerErrorsToFieldErrors', zodErrors: result.error.flatten() }
    });

    return [];
  }

  const { fields } = result.data;
  const fieldValues = Object.values(VALIDATABLE_FIELDS);

  return fieldValues.flatMap((field) => (fields[field] ?? []).map((code) => ({ field, code })));
};
